import { SalesReportError } from '@/components/money/SalesReportError'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { LoadingState } from '@/components/patterns/loading-state'
import { usePathHelper } from '@/hooks/usePathHelper'
import { canDisplaySalesReport } from '@/lib/services/money/money.query'
import { reportQueries, useAccountingJournalOrder } from '@/lib/services/reports/reports.query'
import { salesOrderSchema } from '@/lib/services/sales-orders/sales-order.schema'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { ArrowLeft, BookOpenCheck } from 'lucide-react'
import { SalesOrderJournalResults } from './-journal-results'

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/$salesOrderId_/journal')({
  component: SalesOrderJournalPage,
  params: {
    parse: (params) => salesOrderSchema.pick({ salesOrderId: true }).parse(params),
  },
  loader: async ({ context, params }) => {
    if (
      !context.ability.can('access', 'global') ||
      !context.ability.can('view', 'accounting-journal')
    ) {
      throw notFound()
    }

    // An incomplete or failed journal shows inline on the page instead of failing the route. The router reruns this
    // loader on every navigation, paging included, so it only fills an empty cache; the page query decides freshness.
    await context.queryClient.prefetchQuery({
      ...reportQueries.accountingJournalOrder({
        organizationId: params.orgId,
        filters: { salesOrderId: params.salesOrderId },
      }),
      staleTime: Infinity,
    })
  },
})

function SalesOrderJournalPage() {
  const { orgId, salesOrderId } = Route.useParams()
  const path = usePathHelper()
  const { data, error, isError, isPending, isFetching, refetch } =
    useAccountingJournalOrder(salesOrderId)
  const showJournal = canDisplaySalesReport({ data, error, isError })

  return (
    <div className="space-y-4 pb-6">
      <AdminPageHeader
        icon={BookOpenCheck}
        title="Journal entries"
        description={`Everything Order #${salesOrderId} has posted to the books, from its first sale or payment to today. Internal: for checking the books before they reach QuickBooks.`}
        className="mt-2"
        actions={
          <AdminHeaderActionButton variant="outline" asChild>
            <Link {...path.salesOrders.show(salesOrderId).using({ orgId }).link}>
              <ArrowLeft className="size-4" />
              Back to order
            </Link>
          </AdminHeaderActionButton>
        }
      />

      {isError && (
        <SalesReportError
          error={error}
          orgId={orgId}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          hasCachedReport={showJournal}
        />
      )}
      {isPending ? (
        <LoadingState label="Loading the order's journal…" />
      ) : showJournal && data ? (
        <SalesOrderJournalResults journal={data} orgId={orgId} />
      ) : null}
    </div>
  )
}
